import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Globe, ShieldAlert, Loader2, CheckCircle2, Lock, ArrowRight, X } from 'lucide-react';
import { signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { auth, googleProvider } from '../lib/firebase';
import { bridgeFormsAuth } from '../lib/formsDb';

interface ReauthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (newToken: string) => void;
  currentUserEmail?: string | null;
}

export default function ReauthModal({
  isOpen,
  onClose,
  onSuccess,
  currentUserEmail
}: ReauthModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleReauthenticate = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const credential = GoogleAuthProvider.credentialFromResult(result);
      if (credential) {
        const token = (credential as any).accessToken;
        if (token) {
          localStorage.setItem('google_access_token', token);
          localStorage.setItem('google_access_token_timestamp', Date.now().toString());
          bridgeFormsAuth(credential).catch(() => { /* best-effort bridge */ });
          onSuccess(token);
          onClose();
          return;
        }
      }
      throw new Error("Could not retrieve a fresh access token from Google.");
    } catch (err: any) {
      console.error("Re-authentication failed:", err);
      setError(err?.message || "Failed to re-authenticate with Google. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-md">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="relative w-full max-w-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 md:p-8 shadow-2xl overflow-hidden text-slate-900 dark:text-slate-100"
        >
          {/* Subtle Background Glow */}
          <div className="absolute -top-12 -right-12 w-40 h-40 bg-blue-500/10 dark:bg-blue-500/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-12 -left-12 w-40 h-40 bg-amber-500/10 dark:bg-amber-500/15 rounded-full blur-3xl pointer-events-none" />

          {/* Dismiss button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
            title="Dismiss prompt"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Icon Header */}
          <div className="flex flex-col items-center text-center">
            <div className="relative mb-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-amber-500/10 via-blue-500/10 to-indigo-500/10 dark:from-amber-500/20 dark:via-blue-500/20 dark:to-indigo-500/20 border border-blue-500/20 flex items-center justify-center text-blue-600 dark:text-blue-400 shadow-inner">
                <ShieldAlert className="w-8 h-8 text-amber-500 animate-pulse" />
              </div>
              <span className="absolute -bottom-1 -right-1 p-1 bg-amber-500 text-white rounded-full shadow">
                <Lock className="w-3 h-3" />
              </span>
            </div>

            <h3 className="text-xl font-black tracking-tight text-slate-900 dark:text-white">
              Google Session Expired
            </h3>
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mt-1.5 leading-relaxed max-w-xs">
              Your Google Workspace access token has timed out. Re-authenticate to restore Gmail, Sheets, Calendar, Drive, and Chat features without losing your place.
            </p>

            {currentUserEmail && (
              <div className="mt-3 px-3 py-1 bg-slate-100 dark:bg-slate-800/60 rounded-full border border-slate-200 dark:border-slate-700 text-[11px] font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                <Globe className="w-3 h-3 text-blue-500" />
                <span>Account: <strong className="text-blue-600 dark:text-blue-400">{currentUserEmail}</strong></span>
              </div>
            )}
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/40 rounded-xl text-xs text-red-700 dark:text-red-300 font-medium text-center">
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-6 flex flex-col gap-2.5">
            <button
              onClick={handleReauthenticate}
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 active:scale-[0.99] text-white font-extrabold text-sm py-3 px-4 rounded-xl transition shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Re-authenticating with Google…</span>
                </>
              ) : (
                <>
                  <Globe className="w-4 h-4" />
                  <span>Re-authenticate Now</span>
                  <ArrowRight className="w-4 h-4 ml-auto" />
                </>
              )}
            </button>

            <button
              onClick={onClose}
              disabled={loading}
              className="w-full bg-transparent hover:bg-slate-100 dark:hover:bg-slate-800/60 text-slate-500 dark:text-slate-400 font-bold text-xs py-2 px-4 rounded-xl transition cursor-pointer"
            >
              Remind Me Later
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
